import { PoweredBy } from '@/components/print/PoweredBy';
import { useTranslation } from 'react-i18next';
import { Receipt, X } from 'lucide-react';
import { useCartStore } from '@/stores/cart.store';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency } from '@/lib/utils';
import { aggregateTaxBreakdown, formatTaxLabel } from '@/lib/tax';
import { usePromotionPreview } from '@/hooks/usePromotionPreview';
import { applyPromotionToTotal } from '@/lib/pos-total';
import { useLoyaltyRedeemPreview } from '@/hooks/useLoyaltyRedeemPreview';
import { useLocationStore } from '@/stores/location.store';

/**
 * T-135: Live receipt preview pane shown in the POS right rail.
 * Mirrors the cart contents in a thermal-receipt layout so the cashier can
 * confirm the order at a glance before checkout. Purely presentational —
 * reads cart-store state, never mutates.
 */
export function LiveReceiptPreview({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation('pos');
  const tenant = useAuthStore((s) => s.tenant);

  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal);
  const cartTotalTax = useCartStore((s) => s.totalTax);
  const orderDiscountAmount = useCartStore((s) => s.orderDiscountAmount);
  const lineDiscountTotal = useCartStore((s) => s.lineDiscountTotal);
  const grandTotal = useCartStore((s) => s.grandTotal);
  const itemCount = useCartStore((s) => s.itemCount);
  const customer = useCartStore((s) => s.customer);
  const cartTaxBreakdown = aggregateTaxBreakdown(items.map((i) => i.taxBreakdown));
  const { promotionPreview, promotionDiscount } = usePromotionPreview();
  // POS-I125: the rail quotes the SAME server-priced figures as the cart (POST /sales/preview via
  // the shared owner hook; React Query dedupes the identical request), not the cart store's local
  // maths, which ignores the pro-rata tax base after promotions / order discount. The local sum is
  // only the fallback while the preview loads, fails or is unavailable.
  const { selectedLocationId } = useLocationStore();
  const { pricing: serverPricing } = useLoyaltyRedeemPreview({
    customerId: customer?.id,
    redeemPoints: 0,
    locationId: selectedLocationId,
  });
  const previewTotal = serverPricing
    ? Math.round(serverPricing.totalAmount * 100) / 100
    : applyPromotionToTotal(grandTotal, promotionDiscount);
  const taxBreakdown = serverPricing ? serverPricing.taxBreakdown : cartTaxBreakdown;
  const totalTax = serverPricing ? serverPricing.totalTax : cartTotalTax;

  return (
    <div className="flex h-full flex-col border-s bg-muted/20">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border/50 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary-text shrink-0" />
          <h2 className="text-sm font-semibold">{t('receiptPreview')}</h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title={t('hideReceiptPreview')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Receipt body */}
      <div className="flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-[20rem] rounded-md bg-paper text-paper-foreground shadow-sm px-4 py-4 font-mono text-xs leading-relaxed">
          {/* Store header */}
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-wide break-words">{tenant?.name ?? 'Store'}</p>
            {tenant?.brandTagline && <p className="text-[10px] opacity-70">{tenant.brandTagline}</p>}
            <p className="mt-1 text-[10px] opacity-60">{new Date().toLocaleString()}</p>
            <p className="text-[10px] font-semibold uppercase opacity-70">— {t('previewLabel')} —</p>
          </div>

          <div className="my-2 border-t border-dashed border-paper-foreground/30" />

          {customer && (
            <p className="mb-1 text-[11px]">
              {t('customerLabelReceipt')}: <span className="font-semibold">{customer.name}</span>
            </p>
          )}

          {/* Items */}
          {items.length === 0 ? (
            <p className="py-6 text-center text-[11px] opacity-50">{t('receiptPreviewEmpty')}</p>
          ) : (
            <div className="space-y-1">
              {items.map((item) => (
                <div key={item.id}>
                  <div className="flex justify-between gap-2">
                    <span className="truncate">{item.product.name}</span>
                    <span className="shrink-0 font-semibold">{formatCurrency(item.lineTotal)}</span>
                  </div>
                  <div className="flex justify-between text-[10px] opacity-70">
                    <span>
                      {item.quantity} × {formatCurrency(item.unitPrice)}
                      {item.variant ? ` (${item.variant.name})` : ''}
                    </span>
                    {item.discountAmount > 0 && <span>{t('lineDiscountOff', { amount: formatCurrency(item.discountAmount) })}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="my-2 border-t border-dashed border-paper-foreground/30" />

          {/* Totals */}
          <div className="space-y-0.5">
            <div className="flex justify-between">
              <span>{t('subtotal')}</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {orderDiscountAmount > 0 && (
              <div className="flex justify-between">
                <span>{t('discount')}</span>
                <span>-{formatCurrency(orderDiscountAmount)}</span>
              </div>
            )}
            {promotionPreview.map((promo) => (
              <div key={promo.promotionId} className="flex justify-between">
                <span className="truncate">{promo.promotionName}</span>
                <span>-{formatCurrency(promo.discountAmount)}</span>
              </div>
            ))}
            {taxBreakdown.map((tax) => (
              <div key={`${tax.name}-${tax.rate}`} className="flex justify-between text-[10px] opacity-80">
                <span>{formatTaxLabel(tax)}</span>
                <span>{formatCurrency(tax.amount)}</span>
              </div>
            ))}
            {totalTax > 0 && (
              <div className="flex justify-between">
                <span>{t('tax')}</span>
                <span>{formatCurrency(totalTax)}</span>
              </div>
            )}
          </div>

          <div className="my-2 border-t border-paper-foreground/40" />

          <div className="flex justify-between text-sm font-bold">
            <span>{t('total')}</span>
            <span>{formatCurrency(previewTotal)}</span>
          </div>
          {lineDiscountTotal + orderDiscountAmount + promotionDiscount > 0 && (
            <div className="mt-1 flex justify-between text-[11px]">
              <span>{t('youSaved')}</span>
              <span>{formatCurrency(lineDiscountTotal + orderDiscountAmount + promotionDiscount)}</span>
            </div>
          )}

          <div className="mt-2 text-center text-[10px] opacity-60">
            {itemCount} {itemCount === 1 ? t('itemSingular') : t('itemPlural')}
          </div>
          <PoweredBy />
        </div>
      </div>
    </div>
  );
}
